
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MapPin, Building2, Globe, ExternalLink } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from '@/components/ui/skeleton';

interface PlaceInfoProps {
  isEditing: boolean;
  address: string;
  city: string;
  country: string;
  onAddressChange: (address: string) => void;
  onCityChange: (city: string) => void;
  onCountryChange: (country: string) => void;
}

export function PlaceInfo({
  isEditing,
  address,
  city,
  country,
  onAddressChange,
  onCityChange,
  onCountryChange,
}: PlaceInfoProps) {
  const navigate = useNavigate();

  const { data: destination, isLoading } = useQuery({
    queryKey: ['destination-by-city', city],
    queryFn: async () => {
      if (!city) return null;
      
      const { data, error } = await supabase
        .from('destinations')
        .select('id, name, countries!inner(name)')
        .ilike('name', city)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching destination:', error);
        return null;
      }

      return data;
    },
    enabled: !!city,
  });

  const handleNavigateToDestination = () => {
    if (destination) {
      const countryName = destination.countries?.[0]?.name || country;
      const countrySlug = countryName.toLowerCase().replace(/\s+/g, '-');
      const nameSlug = destination.name.toLowerCase().replace(/\s+/g, '-');
      navigate(`/destinations/${countrySlug}/${nameSlug}/${destination.id}`);
    }
  };

  if (isLoading && city) {
    return (
      <Card className="hover:shadow-md transition-shadow duration-200">
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex flex-col items-center gap-3">
              <MapPin className="w-6 h-6 text-purple-600" />
              <Skeleton className="h-5 w-32" />
            </div>
            <div className="flex flex-col items-center gap-3">
              <Building2 className="w-6 h-6 text-purple-600" />
              <Skeleton className="h-5 w-32" />
            </div>
            <div className="flex flex-col items-center gap-3">
              <Globe className="w-6 h-6 text-purple-600" />
              <Skeleton className="h-5 w-32" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex flex-col items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <MapPin className="w-6 h-6 text-purple-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Address</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isEditing ? (
              <Input
                value={address || ''}
                onChange={(e) => onAddressChange(e.target.value)}
                className="text-center"
                placeholder="Street address"
              />
            ) : (
              <span className="text-sm text-center text-gray-700">{address || 'No address provided'}</span>
            )}
          </div>

          <div className="flex flex-col items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Building2 className="w-6 h-6 text-purple-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>City</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isEditing ? (
              <Input
                value={city || ''}
                onChange={(e) => onCityChange(e.target.value)}
                className="text-center"
                placeholder="City"
              />
            ) : destination ? (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="link" 
                      className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1 group"
                      onClick={handleNavigateToDestination}
                    >
                      {city || 'No city provided'}
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>View destination details</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span className="text-sm text-center text-gray-700">{city || 'No city provided'}</span>
            )}
          </div>

          <div className="flex flex-col items-center gap-3">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Globe className="w-6 h-6 text-purple-600" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Country</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isEditing ? (
              <Input
                value={country || ''}
                onChange={(e) => onCountryChange(e.target.value)}
                className="text-center"
                placeholder="Country"
              />
            ) : (
              <span className="text-sm text-center text-gray-700">{country || 'No country provided'}</span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
